import { useEffect, useRef, useCallback } from "react";
import type { HomeyServerMessage, HomeySocketStatus } from "@/types/homey";
import { supabase } from "@/lib/supabase";

interface UseHomeySocketOptions {
  onMessage: (msg: HomeyServerMessage) => void;
  onStatusChange: (status: HomeySocketStatus) => void;
  enabled?: boolean;
}

const BASE_DELAY_MS = 1_000;
const MAX_DELAY_MS = 30_000;
const JITTER_FACTOR = 0.25;

/**
 * Opens a WebSocket connection to /ws?token=<supabase-access-token>.
 *
 * Reconnects automatically with exponential backoff (1s base, x2, cap 30s,
 * +-25% jitter). Resets the backoff counter on a successful open. Stops when
 * the page is unloading. Cleans up fully on unmount.
 *
 * The onMessage/onStatusChange callbacks are held in refs so reconnects never
 * cause resubscription.
 */
export function useHomeySocket({
  onMessage,
  onStatusChange,
  enabled = true,
}: UseHomeySocketOptions): void {
  // Keep callbacks in refs so they can be captured by closures without
  // triggering reconnects when the caller re-renders.
  const onMessageRef = useRef(onMessage);
  const onStatusChangeRef = useRef(onStatusChange);
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);
  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  }, [onStatusChange]);

  const retryCountRef = useRef(0);
  const socketRef = useRef<WebSocket | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unmountedRef = useRef(false);

  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current !== null) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const closeSocket = useCallback(() => {
    if (socketRef.current) {
      // Remove handlers before closing to avoid triggering reconnect
      socketRef.current.onclose = null;
      socketRef.current.onerror = null;
      socketRef.current.onmessage = null;
      socketRef.current.onopen = null;
      socketRef.current.close();
      socketRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!enabled) {
      onStatusChangeRef.current("offline");
      return;
    }

    unmountedRef.current = false;

    async function connect() {
      if (unmountedRef.current) return;

      onStatusChangeRef.current(retryCountRef.current === 0 ? "connecting" : "reconnecting");

      // Retrieve the current Supabase access token
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;

      if (unmountedRef.current) return;

      if (!token) {
        // No session — go offline, no retry (user must sign in first)
        onStatusChangeRef.current("offline");
        return;
      }

      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const url = `${protocol}//${window.location.host}/ws?token=${encodeURIComponent(token)}`;

      const ws = new WebSocket(url);
      socketRef.current = ws;

      ws.onopen = () => {
        if (unmountedRef.current) {
          ws.close();
          return;
        }
        retryCountRef.current = 0;
        onStatusChangeRef.current("open");
      };

      ws.onmessage = (event: MessageEvent) => {
        try {
          const msg = JSON.parse(event.data as string) as HomeyServerMessage;
          onMessageRef.current(msg);
        } catch {
          // Malformed JSON — ignore silently
        }
      };

      ws.onerror = () => {
        // The browser fires onclose immediately after onerror, so let onclose
        // handle the reconnect logic.
      };

      ws.onclose = () => {
        if (unmountedRef.current) return;

        onStatusChangeRef.current("reconnecting");
        scheduleReconnect();
      };
    }

    function scheduleReconnect() {
      if (unmountedRef.current) return;
      const attempt = retryCountRef.current;
      const base = Math.min(BASE_DELAY_MS * Math.pow(2, attempt), MAX_DELAY_MS);
      const jitter = base * JITTER_FACTOR * (Math.random() * 2 - 1);
      const delay = Math.round(base + jitter);
      retryCountRef.current = attempt + 1;

      retryTimerRef.current = setTimeout(() => {
        if (!unmountedRef.current) {
          connect();
        }
      }, delay);
    }

    connect();

    return () => {
      unmountedRef.current = true;
      clearRetryTimer();
      closeSocket();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, clearRetryTimer, closeSocket]);
}
